import { c as _c } from "react/compiler-runtime";
/* eslint-disable @typescript-eslint/no-floating-promises */
import type { NextRouter } from 'next/router';
import type React from 'react';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { useSegmentClientContext } from './client-provider';
import type { Properties } from '../types';
interface Props {
  children: React.ReactNode;
  router: NextRouter;
  appName?: string;
  defaultPageProperties?: Properties;
}

/**
 * Automatically track page calls when the Next.js router changes.
 */
export function RouteTracker(props) {
  const $ = _c(29);
  const {
    children,
    router,
    appName,
    defaultPageProperties: t0
  } = props;
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const defaultPageProperties = t1;
  const {
    client: analytics
  } = useSegmentClientContext();
  const [hasInitialPageView, setHasInitialPageView] = useState(false);
  const {
    pathname,
    route,
    basePath,
    asPath
  } = router;
  const [hasRouteChanged, setRouteChanged] = useState(false);
  const routerIsReady = router.isReady;
  let t2;
  let t3;
  if ($[2] !== appName || $[3] !== asPath || $[4] !== basePath || $[5] !== defaultPageProperties || $[6] !== pathname || $[7] !== route) {
    t3 = {
      pathname,
      asPath,
      route,
      basePath,
      appName,
      ...defaultPageProperties
    };
    $[2] = appName;
    $[3] = asPath;
    $[4] = basePath;
    $[5] = defaultPageProperties;
    $[6] = pathname;
    $[7] = route;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  t2 = t3;
  const pageProperties = t2;
  let t4;
  let t5;
  if ($[9] !== analytics || $[10] !== hasInitialPageView || $[11] !== pageProperties || $[12] !== routerIsReady) {
    t4 = () => {
      if (hasInitialPageView || !routerIsReady) {
        return;
      }
      setHasInitialPageView(true);
      analytics.page({
        properties: pageProperties
      });
    };
    t5 = [analytics, hasInitialPageView, pageProperties, routerIsReady];
    $[9] = analytics;
    $[10] = hasInitialPageView;
    $[11] = pageProperties;
    $[12] = routerIsReady;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  useEffect(t4, t5);
  let t6;
  if ($[15] !== hasInitialPageView || $[16] !== router.events) {
    t6 = () => {
      const onChange = () => {
        if (!hasInitialPageView) {
          return;
        }
        setRouteChanged(true);
      };
      router.events.on("routeChangeComplete", onChange);
      return () => {
        router.events.off("routeChangeComplete", onChange);
      };
    };
    $[15] = hasInitialPageView;
    $[16] = router.events;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== hasInitialPageView || $[19] !== router) {
    t7 = [router, hasInitialPageView];
    $[18] = hasInitialPageView;
    $[19] = router;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  useEffect(t6, t7);
  const isOutboundLink = _temp;
  let t8;
  let t9;
  if ($[21] !== analytics) {
    t8 = () => {
      const handleClick = event => {
        let {
          target
        } = event as any;
        if (target.tagName !== "A") {
          while (target && target.tagName !== "A") {
            target = target.parentElement as HTMLAnchorElement;
          }
        }
        if (target) {
          const href = target.getAttribute("href");
          if (isOutboundLink(href ?? "", event.defaultPrevented)) {
            analytics.page({
              name: "outbound-link",
              properties: {
                href
              }
            });
          }
        }
      };
      document.addEventListener("click", handleClick as unknown as EventListener);
      return () => {
        document.removeEventListener("click", handleClick as unknown as EventListener);
      };
    };
    t9 = [analytics];
    $[21] = analytics;
    $[22] = t8;
    $[23] = t9;
  } else {
    t8 = $[22];
    t9 = $[23];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[24] !== analytics || $[25] !== hasRouteChanged || $[26] !== pageProperties) {
    t10 = () => {
      if (hasRouteChanged) {
        analytics.page({
          properties: pageProperties
        });
        setRouteChanged(false);
      }
    };
    t11 = [hasRouteChanged, pageProperties, analytics];
    $[24] = analytics;
    $[25] = hasRouteChanged;
    $[26] = pageProperties;
    $[27] = t10;
    $[28] = t11;
  } else {
    t10 = $[27];
    t11 = $[28];
  }
  useEffect(t10, t11);
  return children as JSX.Element;
}
function _temp(url, isPrevented) {
  return url.startsWith("http://") || url.startsWith("https://") || !isPrevented;
}